<template>
  <div
    class="bulk-exclude-toolbar"
  >
    <div
      class="bulk-exclude-header"
      @click="$emit('update:bulkToolbarOpen', !bulkToolbarOpen)"
    >
      <div class="d-flex align-center">
        <span class="font-weight-bold">除外操作メニュー</span>
      </div>
      <div class="d-flex align-center">
        <span
          v-if="!bulkToolbarOpen"
          class="bulk-exclude-click-hint mr-2"
        >
          クリックで開く
        </span>
        <v-icon size="18">
          {{ bulkToolbarOpen ? "mdi-menu-up" : "mdi-menu-down" }}
        </v-icon>
      </div>
    </div>

    <div
      v-if="bulkToolbarOpen"
      class="bulk-exclude-content"
    >
      <div class="bulk-exclude-note">
        ※この操作により、相談者向けサービス「みんなの相談検索」と、相談員向けの「類似事例検索」の両方の検索結果から対象の事例が表示されなくなります。
      </div>

      <v-text-field
        v-model="reason"
        density="compact"
        variant="outlined"
        hide-details
        class="mt-2"
        label="変更理由（必須）"
      />

      <div class="bulk-exclude-actions mt-3">
        <!-- 左：チェックボックス -->
        <div class="bulk-exclude-actions-left">
          <label class="d-flex align-center">
            <input
              type="checkbox"
              :checked="allSelected"
              @change="$emit('toggle-all-selection', $event.target.checked)"
            >
            <span class="ml-2">表示中の{{ selectableBulkCount }}件を選択</span>
          </label>
        </div>

        <!-- 中央：選択件数表示 -->
        <div class="bulk-exclude-actions-center">
          <span 
            v-if="selectedActiveCount > 0"
            class="font-weight-bold bulk-exclude-count-active"
          >
            検索対象内 {{ selectedActiveCount }}件
          </span>
          <span 
            v-if="selectedExcludedCount > 0"
            class="font-weight-bold bulk-exclude-count-excluded"
          >
            除外中 {{ selectedExcludedCount }}件
          </span>
        </div>

        <!-- 右：ボタン -->
        <div class="bulk-exclude-actions-right">
          <v-btn
            :disabled="!canUnexclude"
            :loading="submitting"
            variant="outlined"
            size="small"
            prepend-icon="mdi-eye"
            @click="submitBulkUnexclude"
          >
            {{ selectedExcludedCount > 0 ? selectedExcludedCount + '件を検索対象に戻す' : '選択した項目を検索対象に戻す' }}
          </v-btn>
          <v-btn
            :disabled="!canExclude"
            :loading="submitting"
            color="red-darken-2"
            size="small"
            class="ml-2"
            prepend-icon="mdi-eye-off"
            @click="submitBulkExclude"
          >
            {{ selectedActiveCount > 0 ? selectedActiveCount + '件を検索から除外する' : '選択した項目を検索から除外する' }}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import anytimeConsultationApi from "../../../api/anytime_consultations";

const SEARCH_REFRESH_DELAY_MS = 3000;

export default {
  name: "BulkExcludeToolbar",

  props: {
    bulkToolbarOpen: {
      type: Boolean,
      default: false
    },
    anytimeConsultations: {
      type: Array,
      default: () => []
    },
    selectedIds: {
      type: Array,
      default: () => []
    },
    searchQuery: {
      type: Object,
      default: null
    },
    selectionResetKey: {
      type: Number,
      default: 0
    }
  },

  emits: [
    "update:bulkToolbarOpen",
    "toggle-all-selection",
    "clear-selection",
    "refresh-results",
    "show-toast"
  ],

  data: () => ({
    reason: "",
    submitting: false
  }),

  computed: {
    selectableBulkIds() {
      return (this.anytimeConsultations || []).slice(0, 100).map(consultation => consultation.id);
    },
    selectableBulkCount() {
      return this.selectableBulkIds.length;
    },
    allSelected() {
      if (this.selectableBulkIds.length === 0) return false;

      return this.selectableBulkIds.every(id => this.selectedIds.includes(id));
    },
    selectedActiveCount() {
      return (this.anytimeConsultations || []).filter(consultation => this.selectedIds.includes(consultation.id) && !consultation.excluded).length;
    },
    selectedExcludedCount() {
      return (this.anytimeConsultations || []).filter(consultation => this.selectedIds.includes(consultation.id) && consultation.excluded).length;
    },
    canBulkSubmit() {
      return this.selectedIds.length > 0 && this.reason.trim().length > 0 && !this.submitting;
    },
    canExclude() {
      return this.selectedActiveCount > 0 && this.reason.trim().length > 0 && !this.submitting;
    },
    canUnexclude() {
      return this.selectedExcludedCount > 0 && this.reason.trim().length > 0 && !this.submitting;
    }
  },

  watch: {
    selectionResetKey() {
      this.reason = "";
    }
  },

  methods: {
    showToast(message, color = "success") {
      this.$emit("show-toast", { message, color });
    },

    showBulkOperationResultToast(response, successMessage) {
      const warningMessage = response?.warning;
      if (warningMessage) {
        this.showToast(`${successMessage} ${warningMessage}`, "warning");
        return;
      }

      this.showToast(successMessage, "success");
    },
    
    submitBulkExclude() {
      if (!this.canBulkSubmit) return;
      if (!this.searchQuery) return;

      this.submitting = true;
      const targetIds = [...this.selectedIds].slice(0, 100);
      const requestParams = {
        anytime_consultation_ids: targetIds,
        reason: this.reason,
        query: this.searchQuery
      };

      anytimeConsultationApi.bulkExcludeAnytimeConsultations(requestParams).then((response) => {
        this.showBulkOperationResultToast(response, `${targetIds.length}件を検索から除外しました`);
        this.$emit("clear-selection");
        this.$emit("refresh-results", { delayMs: SEARCH_REFRESH_DELAY_MS });
      }).catch((error) => {
        console.log(error);
        const message = error?.response?.data?.message || "一括操作に失敗しました。";
        this.showToast(message, "error");
      }).finally(() => {
        this.submitting = false;
      });
    },

    submitBulkUnexclude() {
      if (!this.canBulkSubmit) return;
      if (!this.searchQuery) return;

      this.submitting = true;
      const targetIds = [...this.selectedIds].slice(0, 100);
      const requestParams = {
        anytime_consultation_ids: targetIds,
        reason: this.reason,
        query: this.searchQuery
      };

      anytimeConsultationApi.bulkUnexcludeAnytimeConsultations(requestParams).then((response) => {
        this.showBulkOperationResultToast(response, `${targetIds.length}件を検索対象に戻しました`);
        this.$emit("clear-selection");
        this.$emit("refresh-results", { delayMs: SEARCH_REFRESH_DELAY_MS });
      }).catch((error) => {
        console.log(error);
        const message = error?.response?.data?.message || "一括操作に失敗しました。";
        this.showToast(message, "error");
      }).finally(() => {
        this.submitting = false;
      });
    }
  }
};
</script>

<style lang="scss">
.bulk-exclude-toolbar {
  border: solid 1px #9ca3af;
  background-color: #fff;
  border-radius: 2px;

  .bulk-exclude-header {
    padding: 11px 12px;
    cursor: pointer;
    background-color: #ffffff;
    color: #374151;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .bulk-exclude-click-hint {
    font-size: 11px;
    color: #9ca3af;
  }

  .bulk-exclude-content {
    border-top: solid 1px #d0d0d0;
    padding: 12px;
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #fff;
  }

  .bulk-exclude-note {
    font-size: 12px;
    color: #005ea8;
    background-color: #eef6ff;
    border: solid 1px #cfe3ff;
    padding: 8px;
  }

  .bulk-exclude-actions {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .bulk-exclude-actions-left {
    flex: 0 0 auto;
  }

  .bulk-exclude-actions-center {
    flex: 1;
    text-align: left;
    display: flex;
    gap: 16px;
    align-items: center;
  }

  .bulk-exclude-count-active {
    color: #005ea8;
  }

  .bulk-exclude-count-excluded {
    color: #666666;
  }

  .bulk-exclude-actions-right {
    flex: 0 0 auto;
    display: flex;
    gap: 8px;
    align-items: center;
  }
}
</style>
